<template>
    <div class="container mt-5 mb-5">
        <div class="row justify-content-center">
            <div class="col-lg-12">
                <div class="card p-4">
                    <!-- Search Section -->
                    <div class="mb-4 p-3 bg-light rounded-3">
                        <div class="row align-items-center">
                            <div class="col-md-3">
                                <label for="fiscalYear" class="form-label fw-bold">회계연도:</label>
                                <input
                                    type="text"
                                    id="fiscalYear"
                                    v-model="state.fiscalYear"
                                    class="form-control"
                                    placeholder="예: 2026"
                                    maxlength="4"
                                />
                            </div>
                            <div class="col-md-6 d-flex align-items-end gap-2 mt-3 mt-md-0">
                                <button
                                    class="btn btn-primary"
                                    @click="handlePreview"
                                    :disabled="!isValidYear"
                                >
                                    <i class="bi bi-search me-1"></i> 미리보기
                                </button>
                                <button
                                    class="btn btn-success"
                                    @click="handleExecute"
                                    :disabled="!state.hasPreviewData"
                                >
                                    <i class="bi bi-check-circle me-1"></i> 실행
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Result Summary -->
                    <div v-if="state.previewResult" class="mb-4 p-3 border rounded-3 bg-white">
                        <h5 class="text-secondary mb-3">미리보기 결과</h5>
                        <div class="row">
                            <div class="col-md-3">
                                <div class="d-flex align-items-center">
                                    <span class="badge bg-primary fs-6 me-2">{{ state.previewResult.updateCount }}</span>
                                    <span>수정 대상</span>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="d-flex align-items-center">
                                    <span class="badge bg-warning fs-6 me-2">{{ state.previewResult.unmappedCount }}</span>
                                    <span>매핑 없음</span>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <span class="text-muted">
                                    원본 리비전: <strong>{{ state.previewResult.old_rev_id }}</strong>
                                    → 대상 리비전: <strong>{{ state.previewResult.target_rev_id }}</strong>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Product Summary Table -->
                    <div v-if="productSummary.length > 0" class="mb-4">
                        <h5 class="text-secondary mb-3">상품별 수정 요약</h5>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th>현재 상품</th>
                                        <th>변경 상품</th>
                                        <th class="text-end" style="width: 100px;">건수</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, idx) in productSummary" :key="idx">
                                        <td>{{ item.old_prod_nm }}</td>
                                        <td>{{ item.new_prod_nm }}</td>
                                        <td class="text-end">{{ item.count }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- To Update Table -->
                    <div v-if="state.toUpdateList.length > 0" class="mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h5 class="text-secondary mb-0">수정 대상 목록</h5>
                            <select v-model="pageSize" class="form-select form-select-sm" style="width: auto;">
                                <option v-for="size in pageSizeOptions" :key="size" :value="size">
                                    {{ size }}건씩 보기
                                </option>
                            </select>
                        </div>
                        <div class="table-responsive">
                            <table class="table table-striped table-hover">
                                <thead class="table-light">
                                    <tr>
                                        <th>영업기회ID</th>
                                        <th>영업기회명</th>
                                        <th>End User</th>
                                        <th>회계연도</th>
                                        <th>현재 상품</th>
                                        <th>변경 상품</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in paginatedToUpdateList" :key="index">
                                        <td>{{ item.sales_id }}</td>
                                        <td>{{ item.sales_nm }}</td>
                                        <td>{{ item.end_user }}</td>
                                        <td>{{ item.fiscal_year }}</td>
                                        <td>{{ item.old_prod_nm }}</td>
                                        <td>{{ item.new_prod_nm }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- Pagination -->
                        <nav v-if="toUpdateTotalPages > 1" aria-label="To update pagination">
                            <ul class="pagination justify-content-center">
                                <li class="page-item" :class="{ disabled: toUpdatePage === 1 }">
                                    <a class="page-link" href="#" @click.prevent="toUpdatePage--">이전</a>
                                </li>
                                <li class="page-item" v-for="page in toUpdateTotalPages" :key="page" :class="{ active: toUpdatePage === page }">
                                    <a class="page-link" href="#" @click.prevent="toUpdatePage = page">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: toUpdatePage === toUpdateTotalPages }">
                                    <a class="page-link" href="#" @click.prevent="toUpdatePage++">다음</a>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    <!-- Unmapped Records Warning -->
                    <div v-if="state.unmappedList.length > 0" class="mb-4">
                        <h5 class="text-warning mb-3">
                            <i class="bi bi-exclamation-triangle me-1"></i>
                            매핑 없는 레코드 (경고)
                        </h5>
                        <div class="alert alert-warning">
                            <p class="mb-2">아래 레코드들은 상품 매핑이 존재하지 않아 수정되지 않습니다.</p>
                        </div>
                        <div class="table-responsive">
                            <table class="table table-striped table-hover">
                                <thead class="table-warning">
                                    <tr>
                                        <th>영업기회ID</th>
                                        <th>영업기회명</th>
                                        <th>End User</th>
                                        <th>회계연도</th>
                                        <th>상품</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in paginatedUnmappedList" :key="index">
                                        <td>{{ item.sales_id }}</td>
                                        <td>{{ item.sales_nm }}</td>
                                        <td>{{ item.end_user }}</td>
                                        <td>{{ item.fiscal_year }}</td>
                                        <td>{{ item.prod_nm }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- Pagination for unmapped -->
                        <nav v-if="unmappedTotalPages > 1" aria-label="Unmapped pagination">
                            <ul class="pagination justify-content-center">
                                <li class="page-item" :class="{ disabled: unmappedPage === 1 }">
                                    <a class="page-link" href="#" @click.prevent="unmappedPage--">이전</a>
                                </li>
                                <li class="page-item" v-for="page in unmappedTotalPages" :key="page" :class="{ active: unmappedPage === page }">
                                    <a class="page-link" href="#" @click.prevent="unmappedPage = page">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: unmappedPage === unmappedTotalPages }">
                                    <a class="page-link" href="#" @click.prevent="unmappedPage++">다음</a>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    <!-- No Data Message -->
                    <div v-if="state.previewResult && state.toUpdateList.length === 0" class="text-center text-muted p-5">
                        <i class="bi bi-info-circle fs-1"></i>
                        <p class="mt-3">수정할 레코드가 없습니다.</p>
                    </div>

                </div>
            </div>
        </div>

        <!-- Confirm Dialog -->
        <Dialog ref="dialogRef" />
    </div>
</template>

<script setup>
import { reactive, ref, computed, watch } from 'vue'
import { previewBulkUpdateProdMapping, executeBulkUpdateProdMapping } from '@/api/prod'
import { useLoading } from '@/composable/Loading'
import Dialog from '@/components/common/modal/Dialog.vue'

const { setIsLoading } = useLoading()
const dialogRef = ref(null)

// Pagination
const toUpdatePage = ref(1)
const unmappedPage = ref(1)
const pageSize = ref(10)
const pageSizeOptions = [10, 20, 50, 100]

// Reset page when pageSize changes
watch(pageSize, () => {
    toUpdatePage.value = 1
    unmappedPage.value = 1
})

// State
const state = reactive({
    fiscalYear: '',
    previewResult: null,
    toUpdateList: [],
    unmappedList: [],
    hasPreviewData: false
})

// Validation: 4-digit year
const isValidYear = computed(() => /^\d{4}$/.test(state.fiscalYear))

// Pagination computed
const toUpdateTotalPages = computed(() => Math.ceil(state.toUpdateList.length / pageSize.value))
const unmappedTotalPages = computed(() => Math.ceil(state.unmappedList.length / pageSize.value))

const paginatedToUpdateList = computed(() => {
    const start = (toUpdatePage.value - 1) * pageSize.value
    return state.toUpdateList.slice(start, start + pageSize.value)
})

const paginatedUnmappedList = computed(() => {
    const start = (unmappedPage.value - 1) * pageSize.value
    return state.unmappedList.slice(start, start + pageSize.value)
})

// Product summary grouped by old_prod_nm + new_prod_nm
const productSummary = computed(() => {
    const summary = {}
    state.toUpdateList.forEach(item => {
        const key = `${item.old_prod_nm}|${item.new_prod_nm}`
        if (!summary[key]) {
            summary[key] = {
                old_prod_nm: item.old_prod_nm,
                new_prod_nm: item.new_prod_nm,
                count: 0
            }
        }
        summary[key].count++
    })
    return Object.values(summary).sort((a, b) => b.count - a.count)
})

// Preview
const handlePreview = async () => {
    setIsLoading(true)
    try {
        const res = await previewBulkUpdateProdMapping({ fiscal_year: state.fiscalYear })
        if (res.code === '0000') {
            state.previewResult = res.rtn
            state.toUpdateList = res.rtn.toUpdate || []
            state.unmappedList = res.rtn.unmapped || []
            state.hasPreviewData = state.toUpdateList.length > 0
            toUpdatePage.value = 1
            unmappedPage.value = 1
        } else {
            alert(res.ex_message || '미리보기 중 오류가 발생했습니다.')
            resetState()
        }
    } catch (err) {
        console.error('Preview error:', err)
        alert('미리보기 중 오류가 발생했습니다.')
        resetState()
    } finally {
        setIsLoading(false)
    }
}

// Execute
const handleExecute = async () => {
    const confirmed = await dialogRef.value.openDialog(
        'confirm',
        `${state.toUpdateList.length}건의 상품정보를 수정하시겠습니까?`
    )
    if (!confirmed) return

    setIsLoading(true)
    try {
        const res = await executeBulkUpdateProdMapping({ fiscal_year: state.fiscalYear })
        if (res.code === '0000') {
            alert(`${res.rtn.updatedCount}건 수정 완료`)
            resetState()
        } else {
            alert(res.ex_message || '수정 중 오류가 발생했습니다.')
        }
    } catch (err) {
        console.error('Execute error:', err)
        alert('수정 중 오류가 발생했습니다.')
    } finally {
        setIsLoading(false)
    }
}

// Reset state
const resetState = () => {
    state.previewResult = null
    state.toUpdateList = []
    state.unmappedList = []
    state.hasPreviewData = false
}
</script>

<style scoped>
.card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.badge {
    min-width: 40px;
}
</style>
